import { useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * ═══ A PHOTOGRAPHER'S FOLDER, UPLOADED IN ONE GO — STUDENTS OR STAFF ═══
 *
 * ⚠ THIS SCREEN EXISTS BECAUSE THE ONE-AT-A-TIME VERSION DOES NOT GET USED. A school photographer
 * hands over a folder of files named by admission number or employee code; uploading them
 * individually is a day of somebody's life, so it does not happen, so there are no photographs, so
 * there are no ID cards.
 *
 * ⚠ AND EVERY FILE IS REPORTED BACK, matched or not. "612 uploaded" out of 640, with no list,
 * leaves a school unable to find the 28 — which means they will not trust the number at all.
 *
 * ⚠ ONE COMPONENT, TWO USES, for the same reason as the import screen: the caps, the outcome
 * table, the distinction between a failure and a note were each learned once, and a second copy
 * would carry the version that existed on the day it was made.
 */

export interface PhotoOutcome {
  fileName: string;
  ok: boolean;
  message: string;
}

export interface PhotoResult<O extends PhotoOutcome> {
  matched: number;
  skipped: number;
  total: number;
  outcomes: O[];
}

export interface BulkPhotoConfig<O extends PhotoOutcome> {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  /** e.g. "/api/students/photos/bulk" */
  uploadPath: string;
  /** Small print under the file picker. */
  smallPrint: ReactNode;
  /** Column heading for the identifier read out of each filename. */
  keyLabel: string;
  /** How to read that identifier off one outcome. */
  keyOf: (o: O) => string | null;
  /** What "everything worked" says. */
  allMatched: string;
}

export function BulkPhotoUpload<O extends PhotoOutcome>({ config }: { config: BulkPhotoConfig<O> }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<PhotoResult<O> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      setResult(await api.postForm<PhotoResult<O>>(config.uploadPath, form));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The upload failed.');
    } finally { setBusy(false); }
  };

  const failures = result?.outcomes.filter((o) => !o.ok) ?? [];
  // ⚠ A successful upload can still carry a note — "1200px wide, larger than an ID card needs".
  // Worth surfacing, but not beside the failures, where it would read as a problem.
  const notes = result?.outcomes.filter((o) => o.ok && o.message !== 'Uploaded.') ?? [];

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{config.eyebrow}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{config.title}</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '68ch' }}>
          {config.intro}
        </p>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={inputRef} type="file" accept=".zip,application/zip" disabled={busy}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }} />
          <button className="btn btn-primary" disabled={!file || busy} onClick={upload}>
            {busy ? 'Uploading…' : 'Upload the folder'}
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '10px 0 0', maxWidth: '62ch' }}>
          {config.smallPrint}
        </p>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 12, color: 'var(--danger)', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {result && (
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 14 }}>
            <Stat n={result.matched} label="photographs saved" />
            <Stat n={result.skipped} label="not saved" tone={result.skipped ? 'bad' : undefined} />
            <Stat n={result.total} label="files in the zip" />
          </div>

          {failures.length > 0 && (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--danger)', marginBottom: 6 }}>
                Not saved
              </div>
              <OutcomeTable rows={failures} keyLabel={config.keyLabel} keyOf={config.keyOf} />
            </>
          )}

          {notes.length > 0 && (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--warn)', margin: '12px 0 6px' }}>
                Saved, but worth knowing
              </div>
              <OutcomeTable rows={notes} keyLabel={config.keyLabel} keyOf={config.keyOf} />
            </>
          )}

          {failures.length === 0 && (
            <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0 }}>{config.allMatched}</p>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone?: 'bad' }) {
  return (
    <div>
      <div style={{
        fontSize: 26, fontWeight: 650, lineHeight: 1.1,
        color: tone === 'bad' ? 'var(--danger)' : 'var(--ink)',
      }}>{n}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}

function OutcomeTable<O extends PhotoOutcome>(
  { rows, keyLabel, keyOf }: { rows: O[]; keyLabel: string; keyOf: (o: O) => string | null },
) {
  // Capped: a zip where nothing matched — the wrong folder, usually — should not render 640 rows.
  const shown = rows.slice(0, 60);
  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>File</th><th style={th}>{keyLabel}</th><th style={th}>What happened</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((o, i) => (
            <tr key={i}>
              <td style={{ ...td, maxWidth: 220, overflowWrap: 'anywhere' }}>{o.fileName}</td>
              <td style={{ ...td, width: 130, color: 'var(--ink-2)' }}>{keyOf(o) || '—'}</td>
              <td style={td}>{o.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          …and {rows.length - shown.length} more, usually the same cause.
        </p>
      )}
    </>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--line)',
  fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-3)',
};
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--line-2)', verticalAlign: 'top' };
